import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plug } from "lucide-react";

import { Button } from "../../components/Button/Button";
import { Modal } from "../../components/Modal/Modal";
import { useToast } from "../../components/Toast/toastContext";
import { ApiError, describeError } from "../../lib/errors";

import {
  type McpClient,
  mcpClientsQueryKey,
  revokeAllMcpClients,
  revokeMcpClient,
} from "./api";
import { environmentWord } from "./environment";
import styles from "./McpAccessPage.module.css";

export type RevokeTarget =
  | { kind: "one"; client: McpClient }
  | { kind: "all"; count: number };

interface Props {
  target: RevokeTarget | null;
  environment: string;
  onClose: () => void;
}

export function RevokeClientModal({ target, environment, onClose }: Props) {
  const qc = useQueryClient();
  const { push } = useToast();

  const mut = useMutation({
    mutationFn: (t: RevokeTarget) =>
      t.kind === "one"
        ? revokeMcpClient(t.client.id)
        : revokeAllMcpClients().then(() => undefined),
    onSuccess: (_data, t) => {
      // The row leaves at once; the refetch behind it only confirms.
      qc.setQueryData<McpClient[]>(mcpClientsQueryKey, (prev) =>
        t.kind === "one" ? prev?.filter((c) => c.id !== t.client.id) : [],
      );
      void qc.invalidateQueries({ queryKey: mcpClientsQueryKey });
      push({ variant: "success", message: "Access revoked" });
      onClose();
    },
    onError: (err: unknown, t) => {
      void qc.invalidateQueries({ queryKey: mcpClientsQueryKey });
      // A 404 is a grant already revoked elsewhere: the outcome asked for, so
      // the dialog closes instead of offering to revoke it again.
      if (err instanceof ApiError && err.status === 404 && t.kind === "one") {
        qc.setQueryData<McpClient[]>(mcpClientsQueryKey, (prev) =>
          prev?.filter((c) => c.id !== t.client.id),
        );
        push({ variant: "info", message: "This client was already revoked." });
        onClose();
        return;
      }
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    },
  });

  if (!target) return null;

  const env = environmentWord(environment);
  const title =
    target.kind === "one"
      ? `Revoke ${target.client.client_name}?`
      : `Revoke all ${target.count} ${target.count === 1 ? "client" : "clients"}?`;

  return (
    <Modal
      open
      onClose={onClose}
      closeDisabled={mut.isPending}
      width="md"
      title={title}
      icon={
        <Plug size={17} aria-hidden="true">
          <line x1="3" y1="3" x2="21" y2="21" />
        </Plug>
      }
      iconTone="danger"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mut.mutate(target)}
            loading={mut.isPending}
          >
            Revoke
          </Button>
        </>
      }
    >
      {target.kind === "one" ? (
        <p className={styles.modalText}>
          {target.client.user.name}&apos;s <b>{target.client.client_name}</b>
          {target.client.redirect_host
            ? ` (${target.client.redirect_host})`
            : ""}{" "}
          loses access to {env} right away. To use it again, they&apos;ll have
          to sign in and authorize it from the app.
        </p>
      ) : (
        <p className={styles.modalText}>
          Every agent loses access to {env} right away. To use them again, each
          person will have to sign in and authorize from their app.
        </p>
      )}
    </Modal>
  );
}
